import { FranchiseeModifierNamedPriceRow } from '@/types/namedPrice.types';
import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { mapNamedPriceRowToModifierTableRow } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/namedPriceModifier.helpers';
import {
  isModifierCompletelyInactiveAcrossSelection,
  modifierBlocksEditActivation,
  modifierHasInvisibleActivePriceState,
  modifierPriceLockWithActivePrice,
  modifierZeroLockWithActivePrice,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { Modifier } from '@/types/menus.types';

type NamedPriceModifierActionButtonState = {
  isDeactivateButtonDisabled: boolean;
  shouldShowDeactivateTooltip: boolean;
  deactivateTooltipMessage: string;
  isActivateEditButtonDisabled: boolean;
  shouldShowActivateEditTooltip: boolean;
  activateEditTooltipMessage: string;
};

function hasMixedZeroPricedSelection(rows: FranchiseeModifierNamedPriceRow[]): boolean {
  if (rows.length < 2) return false;
  const zeroPricedCount = rows.filter((row) => row.isZeroPriced === true).length;
  return zeroPricedCount > 0 && zeroPricedCount < rows.length;
}

/**
 * Any row that is inactive in every selected store group blocks Deactivate.
 * Zero-priced locks are included, even when selected together with a priced modifier.
 */
function hasDeactivateBlockingInactiveNamedPrice(modifiers: Modifier[], selectedStoreGroupNames: string[]): boolean {
  return modifiers.some((modifier) => isModifierCompletelyInactiveAcrossSelection(modifier, selectedStoreGroupNames));
}

export function useNamedPriceModifierActionButtonState(
  selectedRows: FranchiseeModifierNamedPriceRow[],
  selectedRowCount: number,
  selectedStoreGroupNames: string[],
  t: TFunction,
): NamedPriceModifierActionButtonState {
  return useMemo(() => {
    const hasSelection = selectedRowCount > 0;
    const mixedZeroPriced = hasMixedZeroPricedSelection(selectedRows);
    const selectedAsModifiers = selectedRows.map(mapNamedPriceRowToModifierTableRow);
    const priceLockedWithActivePrice = selectedAsModifiers.some(modifierPriceLockWithActivePrice);
    const zeroLockedWithActivePrice = selectedAsModifiers.some(modifierZeroLockWithActivePrice);
    const invisibleActivePriceForEdit = selectedAsModifiers.some(modifierHasInvisibleActivePriceState);
    const mandatorySelected = selectedRows.some((row) => row.isMandatory === true);
    const deactivateBlockingInactive = hasDeactivateBlockingInactiveNamedPrice(
      selectedAsModifiers,
      selectedStoreGroupNames,
    );

    const isDeactivateButtonDisabled = !hasSelection || mandatorySelected || deactivateBlockingInactive;
    const shouldShowDeactivateTooltip = mandatorySelected || deactivateBlockingInactive;
    const deactivateTooltipMessage = mandatorySelected
      ? t('deactivateModifiers.mandatoryModifiersSelectedTooltip')
      : t('deactivateModifiers.inactiveModifiersSelectedTooltip');

    const activateEditBlocked = mixedZeroPriced || selectedAsModifiers.some(modifierBlocksEditActivation);
    let activateEditTooltipMessage = '';
    if (mixedZeroPriced) {
      activateEditTooltipMessage = t('menu.tooltips.editModifierPriceMixedZeroPriced');
    } else if (priceLockedWithActivePrice) {
      activateEditTooltipMessage = t('menu.tooltips.editModifierPricePriceLock');
    } else if (zeroLockedWithActivePrice) {
      activateEditTooltipMessage = t('menuDetails.modifierRow.zeroPriced');
    } else if (invisibleActivePriceForEdit) {
      activateEditTooltipMessage = t('menu.tooltips.editModifierPriceInvisibleActive');
    }

    return {
      isDeactivateButtonDisabled,
      shouldShowDeactivateTooltip,
      deactivateTooltipMessage,
      isActivateEditButtonDisabled: !hasSelection || activateEditBlocked,
      shouldShowActivateEditTooltip: hasSelection && activateEditBlocked,
      activateEditTooltipMessage,
    };
  }, [selectedRows, selectedRowCount, selectedStoreGroupNames, t]);
}
